import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import RouteCard from './RouteCard';
import RouteModal from './RouteModal';
import './ViewRoutes.css';

const parseDifficulty = (difficulty) => {
  const gradeScale = {
    '4': 1, '4+': 2, '5': 3, '5+': 4,
    '6a': 5, '6a+': 6, '6b': 7, '6b+': 8,
    '6c': 9, '6c+': 10, '7a': 11, '7a+': 12,
    '7b': 13, '7b+': 14, '7c': 15, '7c+': 16,
    '8a': 17, '8a+': 18, '8b': 19, '8b+': 20,
    '8c': 21, '8c+': 22, '9a': 23,
  };
  return gradeScale[String(difficulty || '').trim().toLowerCase()] || 0;
};

const ViewRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [filterBy, setFilterBy] = useState('popularity'); // Default filter set to popularity
  const [difficultyRange, setDifficultyRange] = useState({ low: '', high: '' }); // State for difficulty range filter
  const [routeName, setRouteName] = useState(''); // State for route name search

  useEffect(() => {
    let active = true;
    axios
      .get('https://bouldering-backend.vercel.app/api/routes')
      .then((response) => {
        if (!active) return;
        if (!Array.isArray(response.data)) {
          throw new Error('Invalid routes response');
        }
        const data = response.data
          .filter((route) => route && typeof route === 'object')
          .map((route) => ({
          ...route,
          name: typeof route.name === 'string' ? route.name : 'Unnamed route',
          difficulty: typeof route.difficulty === 'string' ? route.difficulty : '',
          comment: typeof route.comment === 'string' ? route.comment : '',
          completions: Array.isArray(route.completions)
            ? route.completions.filter((completion) => completion && typeof completion === 'object')
            : [],
          holds: Array.isArray(route.holds)
            ? route.holds.filter((hold) => hold && typeof hold === 'object')
            : [],
          createdAt: new Date(route.createdAt),
          }));
        setRoutes(data);
        setErrorMessage('');
      })
      .catch((error) => {
        if (!active) return;
        console.error('Error fetching routes', error);
        setErrorMessage('Failed to fetch routes. Please try again later.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const applyFilter = useCallback(() => {
    let filtered = [...routes];

    // Apply name filter if a name is entered
    if (routeName.trim()) {
      filtered = filtered.filter((route) =>
        route.name.toLowerCase().includes(routeName.toLowerCase())
      );
    }

    // Apply difficulty range filter if selected
    const lowDifficulty = parseDifficulty(difficultyRange.low);
    const highDifficulty = parseDifficulty(difficultyRange.high);
    if (lowDifficulty) {
      filtered = filtered.filter((route) => {
        const routeDifficulty = parseDifficulty(route.difficulty);
        return routeDifficulty >= lowDifficulty;
      });
    }
    if (highDifficulty) {
      filtered = filtered.filter((route) => parseDifficulty(route.difficulty) <= highDifficulty);
    }

    // Apply sorting based on the filterBy selection
    if (filterBy === 'popularity') {
      filtered.sort((a, b) => b.completions.length - a.completions.length);
    } else if (filterBy === 'grade-low-high') {
      filtered.sort((a, b) => parseDifficulty(a.difficulty) - parseDifficulty(b.difficulty));
    } else if (filterBy === 'grade-high-low') {
      filtered.sort((a, b) => parseDifficulty(b.difficulty) - parseDifficulty(a.difficulty));
    } else if (filterBy === 'newest') {
      filtered.sort((a, b) => b.createdAt - a.createdAt);
    }

    setFilteredRoutes(filtered);
  }, [difficultyRange, filterBy, routeName, routes]);

  useEffect(() => {
    // Reapply filter when filterBy, difficultyRange, routeName, or routes change
    applyFilter();
  }, [applyFilter]);

  useEffect(() => {
    if (selectedRoute && !filteredRoutes.some((route) => route._id === selectedRoute._id)) {
      setSelectedRoute(null);
    }
  }, [filteredRoutes, selectedRoute]);

  const handleCloseModal = () => {
    setSelectedRoute(null);
  };

  const handleRouteUpdated = (updatedRoute) => {
    setRoutes((currentRoutes) =>
      currentRoutes.map((route) => (route._id === updatedRoute._id ? updatedRoute : route))
    );
    setSelectedRoute(updatedRoute);
  };

  return (
    <div className="view-routes-container">
      <h1>View Routes</h1>
      <div className="filters-bar">
        <input
          aria-label="Search routes by name"
          type="text"
          placeholder="Search by name"
          value={routeName}
          onChange={e => setRouteName(e.target.value)}
          className="filter-input"
        />
        <div className="difficulty-range">
          <input
            aria-label="Minimum grade"
            type="text"
            placeholder="Min grade (e.g., 6a)"
            value={difficultyRange.low}
            onChange={e => setDifficultyRange({ ...difficultyRange, low: e.target.value })}
            className="filter-input"
          />
          <span style={{ margin: '0 6px' }}>-</span>
          <input
            aria-label="Maximum grade"
            type="text"
            placeholder="Max grade (e.g., 7b)"
            value={difficultyRange.high}
            onChange={e => setDifficultyRange({ ...difficultyRange, high: e.target.value })}
            className="filter-input"
          />
        </div>
        <select
          aria-label="Sort routes by"
          value={filterBy}
          onChange={e => setFilterBy(e.target.value)}
          className="filter-select"
        >
          <option value="popularity">Popularity</option>
          <option value="grade-low-high">Grade (Low to High)</option>
          <option value="grade-high-low">Grade (High to Low)</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {loading && <p role="status">Loading routes...</p>}
      {errorMessage && <p className="error-message" role="alert">{errorMessage}</p>}
      {!loading && !errorMessage && filteredRoutes.length === 0 && (
        <p>No routes match your filters.</p>
      )}
      <div className="route-cards-container">
        {filteredRoutes.map((route, idx) => (
          <RouteCard
            key={route._id}
            route={route}
            onClick={() => setSelectedRoute(route)}
            onPrev={idx > 0 ? () => setSelectedRoute(filteredRoutes[idx - 1]) : undefined}
            onNext={idx < filteredRoutes.length - 1 ? () => setSelectedRoute(filteredRoutes[idx + 1]) : undefined}
            showPrev={idx > 0}
            showNext={idx < filteredRoutes.length - 1}
          />
        ))}
      </div>
      {selectedRoute && (
        <RouteModal
          route={selectedRoute}
          onClose={handleCloseModal}
          routes={filteredRoutes}
          setSelectedRoute={setSelectedRoute}
          onRouteUpdated={handleRouteUpdated}
        />
      )}
    </div>
  );
};

export default ViewRoutes;
